import type { ConfigGraphCard, Freq } from "../types/graph"

/**
 * Grains RÉELLEMENT servis par dapi pour les variables dérivées de la refonte
 * Enedis (DEV-1481, DEV-1505 — audit « Du dû au justifié » 27/08).
 *
 * La config des cartes de graphiques (`ConfigGraph`, servie par api-metier
 * `/graph`) peut promettre plus de grains que la route de lecture dapi n'en
 * sert : l'onglet reste sélectionnable à ces grains et le client voit un cadre
 * vide inexpliqué. Ce catalogue borne la promesse côté front, quelle que soit
 * la config reçue.
 *
 * `duree_elec_depassementpuissance` : dérivé produit à l'ÉCRITURE au grain
 * JOURNALIER (Δ jour à jour de l'index DD, sommé par classe TURPE — dapi
 * `modules/source/datav2_enedis.py`), puis ACCUMULÉ en hebdo/mensuel à la
 * lecture (`get_data_source_weekly.py`, `get_data_source_monthly.py`). Les
 * lecteurs `raw` et `hourly` ne le servent PAS (vérifié le 27/08 sur dapi
 * origin/main) : les promettre fabriquait des cadres vides.
 */
export const DAPI_SERVED_FREQS_BY_VARIABLE: Record<string, readonly Freq[]> = {
    duree_elec_depassementpuissance: ["daily", "weekly", "monthly"],
}

/**
 * Restreint les fréquences promises par une carte de graphiques aux grains
 * réellement servis par dapi pour ses variables (cf.
 * `DAPI_SERVED_FREQS_BY_VARIABLE`).
 *
 * - Un graphique sans variable restreinte est rendu TEL QUEL (même référence).
 * - Un graphique avec variable(s) restreinte(s) garde, dans l'ordre de la
 *   config, les seules fréquences servies pour TOUTES ses variables
 *   restreintes (les variables non listées ne contraignent pas).
 * - Si l'intersection est vide (config ne promettant QUE des grains non
 *   servis), on sert la liste des grains servis de la première variable
 *   restreinte : mieux vaut proposer un grain servi que de laisser l'onglet
 *   sans aucune fréquence valide (le repli `freq[0]` de `DataChartListItem`
 *   bouclerait sur un grain invalide).
 *
 * @param card - Carte de graphiques reçue de l'API
 * @returns La carte, avec les fréquences bornées aux grains servis
 */
export function clampGraphCardFreqsToServed(
    card: ConfigGraphCard
): ConfigGraphCard {
    let changed = false
    const graphList = card.graph_list.map((graph) => {
        const restrictedServedLists = graph.datasets
            .map(
                (dataset) =>
                    DAPI_SERVED_FREQS_BY_VARIABLE[
                        dataset.type?.split(".")[0] ?? ""
                    ]
            )
            .filter((served): served is readonly Freq[] => served !== undefined)

        if (restrictedServedLists.length === 0) {
            return graph
        }

        let freq = graph.freq.filter((f) =>
            restrictedServedLists.every((served) => served.includes(f))
        )
        if (freq.length === 0) {
            freq = [...restrictedServedLists[0]]
        }

        if (
            freq.length === graph.freq.length &&
            freq.every((f, i) => f === graph.freq[i])
        ) {
            return graph
        }
        changed = true
        return { ...graph, freq }
    })

    return changed ? { ...card, graph_list: graphList } : card
}
